/**
 * Player-facing screens for starting a Leaderboard-mode game: the message screens (no leaderboard
 * running, offline, unreadable rules), the setup form — four seats, each a registered nickname plus a
 * PIN — with inline registration of a new player, and the notice shown when a seat is at its limit.
 *
 * The house rules are the venue's and are never editable here: there is only a read-only preview.
 * PINs live only in the inputs on this screen; nothing stores them.
 */

import { renderBrandHeader } from '../branding';
import { esc } from '../helpers';
import { describeLimits, formatDayMonth } from '../leaderboardAdmin';
import type { LeaderboardPeriodInfo } from '../leaderboardClient';

export interface LeaderboardMessageActions {
  back(): void;
  /** Offered only where trying again could help (offline, a slow connection). */
  retry?(): void;
}

export function renderLeaderboardMessage(
  root: HTMLElement,
  title: string,
  message: string,
  actions: LeaderboardMessageActions | undefined,
): void {
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>${esc(title)}</h1>
      ${message ? `<p class="muted">${esc(message)}</p>` : ''}
      ${
        actions
          ? `<div class="row" style="margin-top:1rem">
               <button type="button" id="lbBackBtn" class="secondary">← Back to Mode Select</button>
               ${actions.retry ? '<button type="button" id="lbRetryBtn">Try again</button>' : ''}
             </div>`
          : ''
      }
    </div>
  `;
  if (!actions) return;
  root.querySelector<HTMLButtonElement>('#lbBackBtn')!.addEventListener('click', () => actions.back());
  root.querySelector<HTMLButtonElement>('#lbRetryBtn')?.addEventListener('click', () => actions.retry!());
}

export interface LeaderboardSetupActions {
  /**
   * Creates a User ID; the view shows the returned message inline so nothing already typed is lost.
   * `pending` = the request is waiting for staff, so the person cannot sit at a table yet.
   */
  register(nickname: string, pin: string): Promise<{ ok: boolean; message: string; pending?: boolean }>;
  /** Opens the venue's house rules, read-only. */
  showRules(): void;
  /** Opens the top-50 board for this period. */
  viewBoard(): void;
  /** Opens the screen where a player chooses a new PIN. */
  changePin(): void;
  /** Opens a table the players sign in to on their own phones — the only way to start a leaderboard game. */
  signInOnPhones(): void;
  back(): void;
}

export function describePeriodForPlayers(period: LeaderboardPeriodInfo): string {
  return `${formatDayMonth(period.startsAt)} → ${formatDayMonth(period.endsAt - 1)} · ${describeLimits(period)}`;
}

export function renderLeaderboardSetup(
  root: HTMLElement,
  period: LeaderboardPeriodInfo,
  error: string | undefined,
  actions: LeaderboardSetupActions,
  /** A confirmation to show above the buttons (e.g. after choosing a new PIN). */
  notice?: string,
): void {
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Leaderboard game</h1>
      <p class="muted">
        Scores from this game go on the ${esc(period.venue)} leaderboard. The house rules are set by the venue
        and can't be changed. Every player needs a nickname and PIN, and signs in on their own phone.
      </p>
      <div class="lb-note">${esc(describePeriodForPlayers(period))}</div>

      ${error ? `<div class="error-box">${esc(error)}</div>` : ''}
      ${notice ? `<div class="lb-note">${esc(notice)}</div>` : ''}

      <button id="registerToggleBtn" class="secondary" type="button" style="width:100%;margin-top:0.75rem">New player? Create a User ID</button>

      <div id="registerBox" style="display:none;margin-top:0.75rem">
        <h2>Create a User ID</h2>
        <p class="muted">Pick a nickname other players will see on the board, and a 4-digit PIN only you know. You can play as soon as you sign in at a table.</p>
        <p class="muted" id="joinNotice">
          We keep your nickname (shown on the leaderboard and on the venue's TV), your PIN in scrambled form that nobody can read
          back, and your game results, only to run the leaderboard and check that results are fair. We do not collect your name,
          email address or phone number. Everything is deleted as set out in the Terms of Use: games 90 days after a period ends,
          and an account after 90 days without playing, or sooner if you ask venue staff. By creating a User ID you agree to this.
        </p>
        <label for="regNick">Nickname</label>
        <input type="text" id="regNick" maxlength="20" autocomplete="off" />
        <label for="regPin">PIN (4 digits)</label>
        <input type="password" id="regPin" inputmode="numeric" maxlength="4" autocomplete="off" />
        <label for="regPin2">PIN again</label>
        <input type="password" id="regPin2" inputmode="numeric" maxlength="4" autocomplete="off" />
        <div id="regStatus" style="margin-top:0.5rem"></div>
        <button id="registerBtn" type="button" class="secondary" style="width:100%;margin-top:0.5rem">Send request</button>
      </div>

      <button id="phonesBtn" type="button" style="width:100%;margin-top:1rem">Players sign in on their own phones</button>
      <p class="muted" style="margin:0.5rem 0 0">
        Open a table and show its QR code. Everyone signs in with their own nickname and PIN, so nobody types a PIN on
        someone else's screen.
      </p>

      <button id="rulesBtn" class="ghost" type="button" style="width:100%;margin-top:1rem">House rules (set by the venue)…</button>
      <button id="boardBtn" class="ghost" type="button" style="width:100%;margin-top:0.5rem">View the leaderboard</button>
      <button id="changePinBtn" class="ghost" type="button" style="width:100%;margin-top:0.5rem">Change my PIN</button>
      <button id="backBtn" class="ghost" type="button" style="width:100%;margin-top:0.5rem">← Back to Mode Select</button>
    </div>
  `;

  root.querySelector<HTMLButtonElement>('#phonesBtn')!.addEventListener('click', () => actions.signInOnPhones());
  root.querySelector<HTMLButtonElement>('#rulesBtn')!.addEventListener('click', () => actions.showRules());
  root.querySelector<HTMLButtonElement>('#boardBtn')!.addEventListener('click', () => actions.viewBoard());
  root.querySelector<HTMLButtonElement>('#changePinBtn')!.addEventListener('click', () => actions.changePin());
  root.querySelector<HTMLButtonElement>('#backBtn')!.addEventListener('click', () => actions.back());

  const box = root.querySelector<HTMLElement>('#registerBox')!;
  root.querySelector<HTMLButtonElement>('#registerToggleBtn')!.addEventListener('click', () => {
    box.style.display = box.style.display === 'none' ? '' : 'none';
    if (box.style.display !== 'none') root.querySelector<HTMLInputElement>('#regNick')!.focus();
  });

  const status = root.querySelector<HTMLElement>('#regStatus')!;
  const say = (text: string, isError: boolean) => {
    status.innerHTML = isError ? `<div class="error-box">${esc(text)}</div>` : `<div class="lb-note">${esc(text)}</div>`;
  };
  const registerBtn = root.querySelector<HTMLButtonElement>('#registerBtn')!;
  registerBtn.addEventListener('click', () => {
    const nickname = root.querySelector<HTMLInputElement>('#regNick')!.value.trim();
    const pin = root.querySelector<HTMLInputElement>('#regPin')!.value.trim();
    const again = root.querySelector<HTMLInputElement>('#regPin2')!.value.trim();
    if (!nickname) return say('Choose a nickname.', true);
    if (!/^\d{4}$/.test(pin)) return say('A PIN is exactly 4 digits.', true);
    if (pin !== again) return say('The two PINs are different.', true);
    registerBtn.disabled = true;
    void actions.register(nickname, pin).then((result) => {
      registerBtn.disabled = false;
      say(result.message, !result.ok);
      // The PINs are forgotten straight away; the nickname stays on screen only when the request failed, so it can be fixed.
      if (result.ok) for (const id of ['regNick', 'regPin', 'regPin2']) root.querySelector<HTMLInputElement>(`#${id}`)!.value = '';
    });
  });
}

/** Shown after the relay accepts the start but says one or more seats are at their limit. */
export function renderLeaderboardNotice(
  root: HTMLElement,
  lines: string[],
  actions: { begin(): void; cancel(): void },
): void {
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Not everyone's score will count</h1>
      <p class="muted">You can play this game, but it will not be added to the leaderboard for:</p>
      <ul>${lines.map((l) => `<li>${esc(l)}</li>`).join('')}</ul>
      <p class="muted">It still counts as normal for the other players.</p>
      <div class="row" style="margin-top:1rem">
        <button type="button" id="lbCancelBtn" class="secondary">Cancel</button>
        <button type="button" id="lbBeginBtn">Start the game</button>
      </div>
    </div>
  `;
  root.querySelector<HTMLButtonElement>('#lbCancelBtn')!.addEventListener('click', () => actions.cancel());
  root.querySelector<HTMLButtonElement>('#lbBeginBtn')!.addEventListener('click', () => actions.begin());
}

export interface ChangePinParams {
  nickname: string;
  /** The current PIN — or the temporary one staff gave, when the change is being asked for. */
  pin: string;
  newPin: string;
  newPin2: string;
}

/**
 * Choosing a new PIN. `forced` = staff unlocked the account or gave a temporary PIN, so the player is sent here before they
 * can play: the nickname and the PIN they just typed are already known, and only the new PIN is asked for. Otherwise (the
 * "Change my PIN" button) it asks for the nickname and the current PIN too. Nothing on this screen is stored.
 */
export function renderChangePin(
  root: HTMLElement,
  opts: { nickname: string; currentPin?: string; forced: boolean },
  error: string | undefined,
  actions: { submit(params: ChangePinParams): void; back(): void },
): void {
  const knownCurrent = opts.forced && opts.currentPin !== undefined;
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>${opts.forced ? 'Choose your own PIN' : 'Change my PIN'}</h1>
      <p class="muted">
        ${
          opts.forced
            ? `${esc(opts.nickname)}, the staff have unlocked your account. Choose a new 4-digit PIN that only you know — you can play as soon as you have.`
            : 'Enter your nickname and your current PIN, then choose a new 4-digit PIN.'
        }
      </p>
      ${error ? `<div class="error-box">${esc(error)}</div>` : ''}
      ${
        opts.forced
          ? ''
          : `<label for="cpNick">Nickname</label>
             <input type="text" id="cpNick" autocomplete="off" value="${esc(opts.nickname)}" />`
      }
      ${
        knownCurrent
          ? ''
          : `<label for="cpCurrent">${opts.forced ? 'The PIN you were given' : 'Current PIN'}</label>
             <input type="password" id="cpCurrent" inputmode="numeric" maxlength="4" autocomplete="off" />`
      }
      <label for="cpNew">New PIN (4 digits)</label>
      <input type="password" id="cpNew" inputmode="numeric" maxlength="4" autocomplete="off" />
      <label for="cpNew2">New PIN again</label>
      <input type="password" id="cpNew2" inputmode="numeric" maxlength="4" autocomplete="off" />
      <div class="row" style="margin-top:1rem">
        <button type="button" id="cpBackBtn" class="secondary">Back</button>
        <button type="button" id="cpSaveBtn">Save new PIN</button>
      </div>
    </div>
  `;
  const value = (id: string) => root.querySelector<HTMLInputElement>(`#${id}`)?.value.trim() ?? '';
  root.querySelector<HTMLButtonElement>('#cpBackBtn')!.addEventListener('click', () => actions.back());
  const save = root.querySelector<HTMLButtonElement>('#cpSaveBtn')!;
  save.addEventListener('click', () => {
    save.disabled = true;
    actions.submit({
      nickname: opts.forced ? opts.nickname : value('cpNick'),
      pin: knownCurrent ? opts.currentPin! : value('cpCurrent'),
      newPin: value('cpNew'),
      newPin2: value('cpNew2'),
    });
  });
}
